import { getds } from 'ds'
import { logWarning } from 'shared-code/log'
import { useState, useCallback, useRef, useEffect } from 'react'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import { NotificationsContext } from 'shared-components/contexts/NotificationsContext'
import { DSFacade } from 'types.aa.ds'

interface UseSubmitParams<T> {
  dataSource: DSFacade
  params: T
}

export interface UseSubmitProps<T> {
  submitting: boolean
  submit: (params: T) => Promise<boolean>
}

export function useSubmit<T>(
  handler: (v: UseSubmitParams<T>) => Promise<boolean>
): UseSubmitProps<T> {
  const {
    common: { strings },
  } = CommonDataContext.useContainer()
  const { enqueue } = NotificationsContext.useContainer()
  const [submitting, setSubmitting] = useState(false)
  const { ts } = CommonDataContext.useContainer()
  const storedHandler = useRef<typeof handler>()

  useEffect(() => {
    storedHandler.current = handler
  }, [handler])

  const submit = useCallback(
    async (params: T): Promise<boolean> => {
      if (!storedHandler.current) {
        enqueue({ msg: strings.errGeneral, duration: 3000, variant: 'error' })
        return false
      }

      setSubmitting(true)

      // `try/finally`, not a bare await — see the note in the SoundGuys twin. A rejection
      // here would skip both `setSubmitting(false)` and the toast, leaving the widget
      // dimmed and `pointer-events: none` forever with nothing shown to the reader. AA is
      // not currently reaching a throw on this path; this keeps it that way by construction.
      let success = false
      try {
        success = await storedHandler.current({ dataSource: await getds({ ts }), params })
      } catch (e) {
        logWarning('Submit handler threw', e)
      } finally {
        setSubmitting(false)
      }

      if (success) {
        enqueue({ msg: strings.msgSuccess, duration: 3000, variant: 'success' })
      } else {
        enqueue({ msg: strings.errGeneral, duration: 3000, variant: 'error' })
      }

      return success
    },
    [ts, strings, enqueue]
  )

  return { submit, submitting }
}
